// Legacy-vocabulary layer: the Creator feature still names things "creator
// session" while the database names them workspaces + conversations. The
// session/workspace flattening is gone — these methods now return the real
// entities — so what remains is message and event shape translation plus the
// legacy method names. Quarantined on purpose: the remaining translation goes
// away once the feature adopts workspace vocabulary. Do not add non-legacy
// logic here; generic workspace behavior belongs in creator-workspaces.ts.
import { supabase, uid, isRecord, toRecord, toUnknownList } from './shared'
import type { RecordLike } from './shared'
import {
  buildDefaultCreatorTitle,
  commitCreatorWorkspace,
  getConversationById,
  getCreatorWorkspaceLoad,
  ensureCreatorWorkspace,
  mapDraftWorkspace,
  mapEditableEntity,
  mapWorkspaceConversation,
  mapWorkspaceEvent,
  mapWorkspaceMessage,
  rewindWorkspaceConversation,
} from './creator-workspaces'
import type {
  CreatorMessage,
  CreatorSession,
  CreatorSessionEvent,
  DraftWorkspace,
  EditableEntity,
  WorkspaceConversation,
  WorkspaceConversationEvent,
  WorkspaceConversationMessage,
} from '@/types/domain'
import type {
  CreatorMessageCreateData,
  CreatorMessageUpdateData,
  CreatorSessionBatchEventData,
  CreatorSessionCreateData,
  CreatorSessionEventCreateData,
  CreatorSessionMessagesOptions,
  CreatorSessionUpdateData,
} from '../../contract'

type CreatorMessageRow = CreatorMessage & { t?: string }
type CreatorSessionEventRow = CreatorSessionEvent

function sortCreatorMessagesChronologically(messages: CreatorMessageRow[] = []) {
  return [...messages].sort((a, b) => {
    const aTime = a.t || a.created_at || a.updated_at || 0
    const bTime = b.t || b.created_at || b.updated_at || 0
    const diff = new Date(aTime).getTime() - new Date(bTime).getTime()
    if (diff !== 0) return diff
    return String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
  })
}

function sortCreatorEventsChronologically(events: CreatorSessionEventRow[] = []) {
  return [...events].sort((a, b) => {
    const diff = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
    if (diff !== 0) return diff
    return Number(a.id) - Number(b.id)
  })
}

function mapWorkspaceConversationMessageToCreatorMessage(row: WorkspaceConversationMessage): CreatorMessageRow {
  return {
    id: Number(row.id) || 0,
    conversation_id: row.conversation_id,
    content: row.content,
    is_user_author: row.role === 'user',
    is_streaming: row.status === 'streaming',
    is_complete: row.status === 'complete',
    turn_id: row.turn_id,
    stream_id: row.stream_id,
    created_at: row.created_at,
    updated_at: row.updated_at,
    t: row.t || row.created_at,
  }
}

function mapWorkspaceEventToCreatorEvent(row: WorkspaceConversationEvent, userId = ''): CreatorSessionEventRow {
  return {
    id: row.id,
    conversation_id: row.conversation_id,
    user_id: userId,
    event_type: row.event_type,
    parent_event_id: row.parent_event_id,
    turn_id: row.turn_id ?? null,
    tool_name: row.tool_name,
    payload: row.payload,
    created_at: row.created_at,
  }
}

async function getSessionContext(conversationId: number | string): Promise<{
  entity: EditableEntity
  workspace: DraftWorkspace
  conversation: WorkspaceConversation
}> {
  const conversation = await getConversationById(conversationId)
  if (!conversation) throw new Error('Creator session not found')

  const { data: workspaceRow, error: workspaceError } = await supabase
    .from('draft_workspaces')
    .select('*')
    .eq('id', conversation.workspace_id)
    .eq('status', 'active')
    .single()
  if (workspaceError) throw workspaceError
  const workspace = mapDraftWorkspace(workspaceRow)
  if (!workspace) throw new Error('Creator workspace not found')

  const { data: entityRow, error: entityError } = await supabase
    .from('editable_entities')
    .select('*')
    .eq('id', workspace.editable_entity_id)
    .single()
  if (entityError) throw entityError
  const entity = mapEditableEntity(entityRow)
  if (!entity) throw new Error('Creator workspace not found')

  return { entity, workspace, conversation }
}

export async function createCreatorSession(sessionData: CreatorSessionCreateData) {
  const entityType = sessionData.entity_type
  const title = sessionData.title?.trim() || buildDefaultCreatorTitle(entityType)
  const bundle = await ensureCreatorWorkspace(
    entityType,
    sessionData.linked_entity_id ?? null,
    toRecord(sessionData.draft_payload),
    title,
    {
      bot_id: sessionData.bot_id ?? null,
      advanced_prompt_enabled: Boolean(sessionData.advanced_prompt_enabled),
      creator_guidance: sessionData.prompt_setup ?? {},
    },
  )
  if (!bundle.editable_entity || !bundle.workspace || !bundle.conversation) {
    throw new Error('Creator workspace was not created')
  }

  return {
    editable_entity: bundle.editable_entity,
    workspace: bundle.workspace,
    conversation: bundle.conversation,
  }
}

export async function getCreatorConversationState(conversationId: number | string) {
  const [conversation, messages, events] = await Promise.all([
    getConversationById(conversationId),
    getCreatorSessionMessages(conversationId),
    getCreatorSessionEvents(conversationId),
  ])
  if (!conversation) throw new Error('Creator session not found')
  return {
    conversation,
    messages: messages.messages,
    creator_session_events: events.creator_session_events,
  }
}

export async function updateCreatorConversation(conversationId: number | string, sessionData: CreatorSessionUpdateData = {}) {
  const payload: RecordLike = { updated_at: new Date().toISOString() }
  if (sessionData.prompt_setup !== undefined) {
    payload.prompt_setup = sessionData.prompt_setup
  }
  if (sessionData.bot_id !== undefined) {
    payload.bot_id = sessionData.bot_id ?? null
  }
  if (sessionData.advanced_prompt_enabled !== undefined) {
    payload.advanced_prompt_enabled = Boolean(sessionData.advanced_prompt_enabled)
  }
  if (sessionData.title !== undefined) {
    payload.title = sessionData.title.trim()
  }

  const { data, error } = await supabase
    .from('workspace_conversations')
    .update(payload)
    .eq('id', Number(conversationId))
    .select('*')
    .single()
  if (error) throw error
  return { conversation: mapWorkspaceConversation(data)! }
}

export async function updateCreatorSessionWithEvents(
  id: number | string,
  sessionData: CreatorSessionUpdateData = {},
  events: CreatorSessionBatchEventData[] = [],
) {
  const conversationId = Number(id)
  let context: Awaited<ReturnType<typeof getSessionContext>> | null = null
  let conversation: WorkspaceConversation | null = null

  const needsWorkspaceCommit =
    sessionData.draft_payload !== undefined
    || sessionData.title !== undefined
    || sessionData.prompt_setup !== undefined
    || events.length > 0
  const needsConversationUpdate =
    sessionData.bot_id !== undefined
    || sessionData.advanced_prompt_enabled !== undefined

  if (needsWorkspaceCommit || needsConversationUpdate) {
    context = await getSessionContext(conversationId)
    conversation = context.conversation
  }

  let insertedEvents: CreatorSessionEventRow[] = []
  if (needsWorkspaceCommit && context) {
    const entityType = context.entity.entity_type
    const nextDraftPayload = sessionData.draft_payload !== undefined
      ? toRecord(sessionData.draft_payload)
      : toRecord(context.workspace.current_payload)
    const nextTitle = sessionData.title !== undefined
      ? sessionData.title.trim() || buildDefaultCreatorTitle(entityType)
      : context.workspace.title
    const response = await commitCreatorWorkspace({
      workspace_id: context.workspace.id,
      conversation_id: conversationId,
      expected_revision: context.workspace.current_revision,
      draft_payload: nextDraftPayload,
      creator_guidance: sessionData.prompt_setup !== undefined
        ? sessionData.prompt_setup
        : context.workspace.creator_guidance,
      title: nextTitle,
      events,
    })
    context = { ...context, workspace: response.workspace }
    const userId = await uid()
    insertedEvents = sortCreatorEventsChronologically(
      response.events.map((event) => mapWorkspaceEventToCreatorEvent(event, userId)),
    )
  }

  if (needsConversationUpdate && context) {
    const response = await updateCreatorConversation(conversationId, {
      ...(sessionData.bot_id !== undefined ? { bot_id: sessionData.bot_id } : {}),
      ...(sessionData.advanced_prompt_enabled !== undefined
        ? { advanced_prompt_enabled: sessionData.advanced_prompt_enabled }
        : {}),
    })
    conversation = response.conversation
    context = { ...context, conversation }
  }

  return {
    editable_entity: context?.entity ?? null,
    workspace: context?.workspace ?? null,
    conversation: context?.conversation ?? null,
    creator_session_events: insertedEvents,
  }
}

export async function getCreatorSessionMessages(sessionId: number | string, options: CreatorSessionMessagesOptions = {}) {
  let query = supabase
    .from('conversation_messages')
    .select('*')
    .eq('conversation_id', Number(sessionId))
    .order('created_at', { ascending: false })

  if (options?.limit !== undefined && options?.limit !== null) {
    const limitValue = Number(options.limit)
    if (Number.isFinite(limitValue)) {
      query = query.limit(limitValue)
    }
  }

  const { data, error } = await query
  if (error) throw error

  const messages = sortCreatorMessagesChronologically(
    toUnknownList(data)
      .map(mapWorkspaceMessage)
      .map(mapWorkspaceConversationMessageToCreatorMessage),
  )
  return { messages }
}

export async function getCreatorSessionEvents(sessionId: number | string) {
  const userId = await uid()
  const { data, error } = await supabase
    .from('conversation_events')
    .select('*')
    .eq('conversation_id', Number(sessionId))
    .order('created_at', { ascending: true })
    .order('id', { ascending: true })

  if (error) throw error

  return {
    creator_session_events: toUnknownList(data)
      .map(mapWorkspaceEvent)
      .map((event) => mapWorkspaceEventToCreatorEvent(event, userId)),
  }
}

export async function createCreatorSessionEvent(eventData: CreatorSessionEventCreateData) {
  const userId = await uid()
  const { data, error } = await supabase
    .from('conversation_events')
    .insert({
      conversation_id: Number(eventData.conversation_id),
      event_type: eventData.event_type,
      parent_event_id: eventData.parent_event_id ?? null,
      turn_id: eventData.turn_id ?? null,
      tool_name: eventData.tool_name ?? null,
      payload: eventData.payload ?? {},
    })
    .select('*')
    .single()
  if (error) throw error

  return {
    creator_session_event: mapWorkspaceEventToCreatorEvent(mapWorkspaceEvent(data), userId),
  }
}

export async function createCreatorMessage(messageData: CreatorMessageCreateData) {
  const { data, error } = await supabase
    .from('conversation_messages')
    .insert({
      conversation_id: Number(messageData.conversation_id),
      role: messageData.is_user_author ? 'user' : 'assistant',
      content: messageData.content,
      status: messageData.is_streaming
        ? 'streaming'
        : messageData.is_complete === false
          ? 'failed'
          : 'complete',
      turn_id: messageData.turn_id ?? null,
      stream_id: messageData.stream_id ?? null,
    })
    .select('*')
    .single()
  if (error) throw error

  return {
    message: mapWorkspaceConversationMessageToCreatorMessage(mapWorkspaceMessage(data)),
  }
}

export async function updateCreatorMessage(id: number | string, messageData: CreatorMessageUpdateData = {}) {
  const payload: RecordLike = {}

  if (messageData.content !== undefined) {
    payload.content = messageData.content
  }
  if (messageData.is_user_author !== undefined) payload.role = messageData.is_user_author ? 'user' : 'assistant'
  if (messageData.is_streaming !== undefined) {
    payload.status = messageData.is_streaming ? 'streaming' : 'complete'
  }
  if (messageData.is_complete !== undefined) {
    payload.status = messageData.is_complete ? 'complete' : 'failed'
  }
  if (messageData.turn_id !== undefined) {
    payload.turn_id = messageData.turn_id ?? null
  }
  if (messageData.stream_id !== undefined) {
    payload.stream_id = messageData.stream_id ?? null
  }
  payload.updated_at = new Date().toISOString()

  const { data, error } = await supabase
    .from('conversation_messages')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single()

  if (error) throw error

  return {
    message: mapWorkspaceConversationMessageToCreatorMessage(mapWorkspaceMessage(data)),
  }
}

export async function deleteCreatorMessages(fromMessageId: number | string, sessionId: number | string) {
  const { error } = await supabase
    .from('conversation_messages')
    .delete()
    .eq('conversation_id', Number(sessionId))
    .gte('id', Number(fromMessageId))

  if (error) throw error
}

async function refreshCreatorAfterRewind(conversationId: number | string) {
  const conversation = await getConversationById(conversationId)
  if (!conversation) throw new Error('Creator session not found')
  const bundle = await getCreatorWorkspaceLoad(conversation.workspace_id, conversation.id)
  const userId = await uid()
  return {
    editable_entity: bundle.editable_entity,
    workspace: bundle.workspace,
    conversation: bundle.conversation,
    messages: sortCreatorMessagesChronologically(bundle.messages.map(mapWorkspaceConversationMessageToCreatorMessage)),
    creator_session_events: sortCreatorEventsChronologically(
      bundle.events.map((event) => mapWorkspaceEventToCreatorEvent(event, userId)),
    ),
  }
}

export async function deleteCreatorAssistantTurn(sessionId: number | string, messageId: number | string) {
  const { data: messageRow, error: messageError } = await supabase
    .from('conversation_messages')
    .select('*')
    .eq('id', Number(messageId))
    .eq('conversation_id', Number(sessionId))
    .single()
  if (messageError) throw messageError
  const message = mapWorkspaceMessage(messageRow)

  if (message.turn_id) {
    await rewindWorkspaceConversation(Number(sessionId), { turn_id: message.turn_id })
  } else {
    const { error } = await supabase
      .from('conversation_messages')
      .delete()
      .eq('id', Number(messageId))
      .eq('conversation_id', Number(sessionId))
    if (error) throw error
  }

  return refreshCreatorAfterRewind(sessionId)
}

export async function truncateCreatorSessionFromTurn(
  sessionId: number | string,
  creatorTurnId: string,
) {
  await rewindWorkspaceConversation(Number(sessionId), { turn_id: creatorTurnId })
  return refreshCreatorAfterRewind(sessionId)
}

export async function clearCreatorSessionTranscript(sessionId: number | string) {
  const { error: eventsError } = await supabase
    .from('conversation_events')
    .delete()
    .eq('conversation_id', Number(sessionId))
  if (eventsError) throw eventsError

  const { error: messagesError } = await supabase
    .from('conversation_messages')
    .delete()
    .eq('conversation_id', Number(sessionId))
  if (messagesError) throw messagesError

  const context = await getSessionContext(sessionId)
  return {
    editable_entity: context.entity,
    workspace: context.workspace,
    conversation: context.conversation,
    messages: [],
    creator_session_events: [],
  }
}
